import moment from 'moment-timezone';
import type {
    Doc31AuditEvent,
    Doc31CouponSelectionStatus,
} from '../../../types/doc31';
import {
    DOC31_COUPON_STATUS_LABELS,
    describeDoc31Path,
} from '../../../utils/doc31';

const MAX_VALUE_LENGTH = 80;
/** Date and time, then a strict ISO 8601 parse decides. */
const ISO_INSTANT_PREFIX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export interface Doc31HistoryDay {
    key: string;
    heading: string;
    events: Doc31AuditEvent[];
}

export interface Doc31FormattedValue {
    text: string;
    /** The full value when `text` was shortened. */
    title?: string;
}

export interface Doc31ChangeRow {
    path: string;
    label: string;
    before: Doc31FormattedValue;
    after: Doc31FormattedValue;
    /** Absent from `before`: the value is new, nothing to strike through. */
    isNew: boolean;
}

/** Events in their order, grouped by local day of `effectiveAt`. */
export function groupDoc31HistoryByDay(
    events: Doc31AuditEvent[],
    timezone: string
): Doc31HistoryDay[] {
    const days: Doc31HistoryDay[] = [];
    for (const event of events) {
        const local = moment.tz(event.effectiveAt, timezone);
        const key = local.format('YYYY-MM-DD');
        const last = days[days.length - 1];
        if (last?.key === key) {
            last.events.push(event);
        } else {
            days.push({
                key,
                heading: local.format('dddd, MMMM D'),
                events: [event],
            });
        }
    }
    return days;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isLocalizedText(
    value: unknown
): value is { en?: unknown; fr?: unknown } {
    if (!isPlainObject(value)) {
        return false;
    }
    const keys = Object.keys(value);
    return (
        keys.length > 0 &&
        keys.every((key) => key === 'en' || key === 'fr') &&
        keys.every(
            (key) => value[key] === null || typeof value[key] === 'string'
        )
    );
}

interface PacingPeriodLike {
    periodStart: string;
    periodEnd: string;
    targetAwards: number;
}

function isPacingPeriods(value: unknown[]): value is PacingPeriodLike[] {
    return (
        value.length > 0 &&
        value.every(
            (item) =>
                isPlainObject(item) &&
                typeof item.periodStart === 'string' &&
                typeof item.periodEnd === 'string' &&
                typeof item.targetAwards === 'number'
        )
    );
}

interface CouponItemLike {
    language: string;
    title: string;
}

function isCouponItems(value: unknown[]): value is CouponItemLike[] {
    return (
        value.length > 0 &&
        value.every(
            (item) =>
                isPlainObject(item) &&
                typeof item.language === 'string' &&
                typeof item.title === 'string'
        )
    );
}

function formatPeriod({
    periodStart,
    periodEnd,
    targetAwards,
}: PacingPeriodLike) {
    const start = moment(periodStart, 'YYYY-MM-DD');
    const end = moment(periodEnd, 'YYYY-MM-DD');
    let range = start.format('MMM D');
    if (!end.isSame(start, 'day')) {
        range += `–${end.format(end.isSame(start, 'month') ? 'D' : 'MMM D')}`;
    }
    return `${range}: ${targetAwards}`;
}

function formatLocalized(entries: [string, unknown][]): string {
    return entries
        .map(([language, text]) => `${language.toUpperCase()}: ${text ?? '—'}`)
        .join(' / ');
}

function toText(value: unknown, timezone: string): string {
    if (value === null || value === undefined) {
        return '—';
    }
    if (typeof value === 'boolean') {
        return value ? 'On' : 'Off';
    }
    if (typeof value === 'string') {
        return ISO_INSTANT_PREFIX.test(value) &&
            moment(value, moment.ISO_8601, true).isValid()
            ? moment.tz(value, timezone).format('MMM D, HH:mm')
            : value;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (isLocalizedText(value)) {
        return formatLocalized(
            (['en', 'fr'] as const)
                .filter((language) => language in value)
                .map((language) => [language, value[language]])
        );
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return '—';
        }
        if (isPacingPeriods(value)) {
            return value.map(formatPeriod).join(', ');
        }
        if (isCouponItems(value)) {
            return formatLocalized(
                value.map((item) => [item.language, item.title])
            );
        }
    }
    return JSON.stringify(value);
}

export function formatDoc31Value(
    value: unknown,
    timezone: string
): Doc31FormattedValue {
    const text = toText(value, timezone);
    return text.length > MAX_VALUE_LENGTH
        ? { text: `${text.slice(0, MAX_VALUE_LENGTH - 1)}…`, title: text }
        : { text };
}

/**
 * Nested plain objects become dotted paths (`selection.weight`), so a whole
 * document reads field by field; localized text stays one value.
 */
function flatten(
    values: Record<string, unknown>,
    prefix = ''
): Record<string, unknown> {
    const flat: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(values)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (isPlainObject(value) && !isLocalizedText(value)) {
            Object.assign(flat, flatten(value, path));
        } else {
            flat[path] = value;
        }
    }
    return flat;
}

/** One row per changed path: keys of `after`, then any only in `before`. */
export function doc31ChangeRows(
    event: Doc31AuditEvent,
    timezone: string
): Doc31ChangeRow[] {
    const before = flatten(event.before ?? {});
    const after = flatten(event.after ?? {});
    const paths = [
        ...Object.keys(after),
        ...Object.keys(before).filter((path) => !(path in after)),
    ];
    return paths.map((path) => ({
        path,
        label: describeDoc31Path(path),
        before: formatDoc31Value(before[path], timezone),
        after: formatDoc31Value(after[path], timezone),
        isNew: event.before === null,
    }));
}

/** `coupon.created`: "Free coffee · 500 coupons · Active" from the document. */
export function doc31CouponCreatedSummary(event: Doc31AuditEvent): string {
    const after = event.after ?? {};
    const items = Array.isArray(after.items) ? after.items : [];
    const english = items.find(
        (item): item is CouponItemLike =>
            isPlainObject(item) && item.language === 'en'
    );
    const quantity = after.quantity;
    const selection = isPlainObject(after.selection) ? after.selection : {};
    const status = selection.status;
    return [
        english?.title ?? 'Untitled coupon',
        typeof quantity === 'number' ? `${quantity} coupons` : 'Unlimited',
        typeof status === 'string'
            ? DOC31_COUPON_STATUS_LABELS[
                  status as Doc31CouponSelectionStatus
              ] ?? status
            : null,
    ]
        .filter(Boolean)
        .join(' · ');
}
